/**
 * Whether this tab runs an older build of the app than the one served now (F89). Each build
 * carries its own id and writes it into `build.json` beside `index.html` (vite.config.ts);
 * that file is never cached (nginx.conf). A tab left open across an update reads a newer id
 * there, and the app offers a reload instead of failing on answers it can no longer read.
 *
 * In development Vite serves the sources live and there is no build: nothing is compared.
 */

const RUNNING_BUILD: string = import.meta.env.VITE_DMW_BUILD;

/** The served `build.json`'s id, or `null` when it is not one. */
export function buildIdOf(served: unknown): string | null {
  if (typeof served !== "object" || served === null || !("build" in served)) return null;
  return typeof served.build === "string" && served.build !== "" ? served.build : null;
}

/** A served build counts as newer only when it was read and differs from the running one. */
export function isOtherBuild(running: string, served: string | null): boolean {
  return running !== "" && served !== null && served !== running;
}

let newerBuildSeen = false;
const listeners = new Set<() => void>();

/** Called once the served build is found to differ; it stays found until the page reloads. */
export function onNewerBuild(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * Reads the served build and compares it with this tab's. A failed read is no evidence of a
 * newer build, so it answers `false`, and the caller keeps the error it had.
 */
export async function servedBuildIsNewer(): Promise<boolean> {
  if (newerBuildSeen) return true;
  if (RUNNING_BUILD === "") return false;
  let served: string | null;
  try {
    const response = await fetch(`${import.meta.env.BASE_URL}build.json`, { cache: "no-store", credentials: "same-origin" });
    served = response.ok ? buildIdOf(await response.json()) : null;
  } catch {
    return false;
  }
  if (!isOtherBuild(RUNNING_BUILD, served)) return false;
  newerBuildSeen = true;
  for (const listener of listeners) listener();
  return true;
}
